<template>
  <ConfirmModal
    ref="modalRef"
    :title="`Sign out ${deviceLabel}?`"
    description="That device will need a fresh sign-in."
    confirm-label="Sign out"
    :loading="isRevoking"
    @confirm="revoke"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { ConfirmModal, extractErrorMessage, useAlertsStore } from '@tallo/design-system';
import { profileService, SessionDto } from '@tallo/investor/investor';

const emit = defineEmits<{ revoked: [] }>();

const alertsStore = useAlertsStore();

const modalRef = ref<InstanceType<typeof ConfirmModal>>();
const session = ref<SessionDto | null>(null);
const isRevoking = ref(false);

const deviceLabel = computed(() => session.value?.deviceName ?? 'this device');

function show(sessionToRevoke: SessionDto) {
  session.value = sessionToRevoke;
  modalRef.value?.show();
}

async function revoke() {
  if (!session.value || isRevoking.value) return;

  isRevoking.value = true;

  try {
    await profileService.revokeSession(session.value.id);
    alertsStore.success(`${deviceLabel.value} signed out`);
    emit('revoked');
    modalRef.value?.close();
  } catch (error) {
    alertsStore.error(extractErrorMessage(error, 'Could not sign the device out. Please try again.'));
  } finally {
    isRevoking.value = false;
  }
}

defineExpose({ show });
</script>
